'use client'

import { usePathname, useRouter } from 'next/navigation'
import { useTranslations } from 'next-intl'
import { useCallback, useEffect, useMemo, useRef } from 'react'

import useIsAdmin from '~/auth/hooks/useIsAdmin'
import { useGateValue } from '~/configs/statsig'
import { useGetOrg, useOrganizationTree } from '~/data/api/hooks/organizations'
import { isSharedOrg, type CustomerWithPath, type OrgPath } from '~/data/types/organization'
import {
  clearCloakedOrgPath,
  readCloakedOrgPath,
  writeCloakedOrgPath
} from '~/layouts/components/orgHierarchy/cloakSessionStorage'
import resolveRouteAfterOrgSwitch from '~/layouts/components/orgHierarchy/resolveRouteAfterOrgSwitch'
import type { OrgContextValue, OrgNode } from '~/layouts/components/orgHierarchy/types'
import { normalizeAppPath } from '~/navigation/isInAppPath'
import { useCloaking, useCloakingInfo, usePrincipalOrgId } from '~/store/slices/currentOrg'
import { useSnackbarControls } from '~/store/slices/display'

function toOrgNode(
  id: string,
  name: string,
  depth: number,
  parentId: string | null,
  uiAccentColor?: string
): OrgNode {
  return { id, name, depth, parentId, uiAccentColor }
}

/**
 * Session org hierarchy for the hierarchy bar: home/current/path/children plus
 * switchTo / exitToHome. Builds on the existing cloak stack — does not change auth.
 */
function useOrgContext(): OrgContextValue {
  const principalOrgId = usePrincipalOrgId()
  const { cloakedOrgs, isCloaking } = useCloakingInfo()
  const isAdmin = useIsAdmin()
  const hierarchyBarEnabled = useGateValue('org_hierarchy_bar')
  // Skip tree/org fetches when the gate is off (bar is not mounted then, but keep
  // the hook fail-closed if a future caller mounts it while gated).
  const skipFetches = !hierarchyBarEnabled
  const {
    organizations,
    isLoading: isTreeLoading,
    isError: isTreeError
  } = useOrganizationTree({ skip: skipFetches })
  const { org: principalOrg } = useGetOrg(principalOrgId, { skip: skipFetches || !principalOrgId })
  const cloakedLeafId = cloakedOrgs.at(-1)?.id
  const { org: currentOrgDetails } = useGetOrg(cloakedLeafId, {
    skip: skipFetches || !cloakedLeafId
  })
  const cloak = useCloaking()
  const { openSnackbar } = useSnackbarControls()
  const t = useTranslations('orgBar')
  const router = useRouter()
  const pathname = usePathname()
  const restoreDoneRef = useRef(false)
  const pendingRestoreRef = useRef(false)

  const announceViewing = useCallback(
    (orgName: string) => {
      openSnackbar(t('nowViewing', { org: orgName }), { severity: 'info' })
    },
    [openSnackbar, t]
  )

  const orgById = useMemo(() => {
    const map = new Map<string, CustomerWithPath>()
    for (const org of organizations) {
      if (org.id) map.set(org.id, org)
    }
    return map
  }, [organizations])

  const path: OrgNode[] = useMemo(() => {
    const homeName = principalOrg?.organizationNm ?? cloakedOrgs[0]?.name ?? ''
    const homeAccent = principalOrg?.uiAccentColor

    if (!principalOrgId) {
      return []
    }

    // Prefer the live cloak stack (already home→current). Enrich names from the tree.
    const stack = cloakedOrgs.length > 0 ? cloakedOrgs : [{ id: principalOrgId, name: homeName }]

    return stack.map((entry, index) => {
      const fromTree = orgById.get(entry.id)
      const name =
        (index === 0 ? homeName : undefined) ||
        entry.name ||
        fromTree?.organizationNm ||
        (index === stack.length - 1 ? currentOrgDetails?.organizationNm : undefined) ||
        ''
      const parentId = index === 0 ? null : (stack[index - 1]?.id ?? null)
      const accent =
        index === 0
          ? homeAccent
          : index === stack.length - 1
            ? currentOrgDetails?.uiAccentColor
            : undefined
      return toOrgNode(entry.id, name, index, parentId, accent)
    })
  }, [cloakedOrgs, principalOrgId, principalOrg, orgById, currentOrgDetails])

  const homeOrg = path[0] ?? toOrgNode(principalOrgId || '', '', 0, null)
  const currentOrg = path.at(-1) ?? homeOrg

  const children: OrgNode[] = useMemo(() => {
    const childDepth = currentOrg.depth + 1
    return organizations
      .filter((org) => org.id && org.parentOrganizationId === currentOrg.id && !isSharedOrg(org))
      .map((org) => toOrgNode(org.id!, org.organizationNm ?? '', childDepth, currentOrg.id))
  }, [organizations, currentOrg])

  const hasDescendants = useMemo(
    () => organizations.some((org) => org.id && !isSharedOrg(org)),
    [organizations]
  )

  // Empty list while customers load is unknown, not "no descendants" — keep the
  // bar mounted so leaf hide and Owned-by column swap don't flash.
  const canShowBar = isCloaking || hasDescendants || isTreeLoading

  // Gate-off clear of synop.cloakedOrgPath lives in Banners — this hook unmounts
  // with OrgHierarchyBar when the gate flips off, so a clear here never ran.

  // Restore cloak from sessionStorage once principal + org tree are known —
  // must run before persist, and must finish reading storage before any write
  // can clear it. Wait for a trustworthy tree so we can reject paths whose
  // leaf is not a real descendant (e.g. a stale prod-US id after a preview
  // backend switch) without wiping storage on empty/error/non-admin flashes.
  useEffect(() => {
    if (!hierarchyBarEnabled || restoreDoneRef.current || !principalOrgId) return
    if (isTreeLoading) return
    // Non-admin customers query is skipped (empty + not loading) before ACL is
    // ready and for Drivers — wait rather than finalizing/clearing.
    if (!isAdmin) return
    // A failed customers request also looks like a settled empty tree.
    if (isTreeError) return

    const stored = readCloakedOrgPath()
    if (!stored || stored.length <= 1) {
      restoreDoneRef.current = true
      return
    }
    if (stored[0]?.id !== principalOrgId) {
      clearCloakedOrgPath()
      restoreDoneRef.current = true
      return
    }
    if (isCloaking) {
      restoreDoneRef.current = true
      return
    }

    const leaf = stored.at(-1)
    if (!leaf) {
      restoreDoneRef.current = true
      return
    }

    // Admin with a settled empty tree: any multi-step path is invalid.
    if (organizations.length === 0) {
      clearCloakedOrgPath()
      restoreDoneRef.current = true
      return
    }

    // Every step past home must exist in the current backend's customer tree.
    const pathValid = stored.slice(1).every((entry) => orgById.has(entry.id))
    if (!pathValid) {
      clearCloakedOrgPath()
      restoreDoneRef.current = true
      return
    }

    restoreDoneRef.current = true
    pendingRestoreRef.current = true
    cloak({
      id: leaf.id,
      organizationNm: leaf.name,
      path: stored
    })
  }, [
    principalOrgId,
    cloak,
    isCloaking,
    hierarchyBarEnabled,
    isTreeLoading,
    isTreeError,
    isAdmin,
    organizations.length,
    orgById
  ])

  // Persist cloak path per tab only after restore has had a chance to run, and
  // never overwrite storage with a home-only path while a restore cloak is in flight.
  useEffect(() => {
    if (!hierarchyBarEnabled || !restoreDoneRef.current) return
    if (pendingRestoreRef.current) {
      if (!isCloaking) return
      pendingRestoreRef.current = false
    }
    const stored: OrgPath = path.map((node) => ({ id: node.id, name: node.name }))
    writeCloakedOrgPath(stored)
  }, [path, hierarchyBarEnabled, isCloaking])

  const navigateAfterSwitch = useCallback(() => {
    const current = normalizeAppPath(pathname || '/')
    const next = resolveRouteAfterOrgSwitch(current)
    if (next !== current) {
      router.replace(next)
    }
  }, [pathname, router])

  const goHome = useCallback(() => {
    // Prefer `cloak` (not bare `resetCloaking`) so vehicles/sites overview
    // filters reset the same way as mid-path ancestor switches.
    cloak({
      id: homeOrg.id,
      organizationNm: homeOrg.name,
      path: [{ id: homeOrg.id, name: homeOrg.name }]
    })
    clearCloakedOrgPath()
    announceViewing(homeOrg.name)
    navigateAfterSwitch()
  }, [cloak, homeOrg.id, homeOrg.name, announceViewing, navigateAfterSwitch])

  const switchTo = useCallback(
    (orgId: string) => {
      if (!orgId || orgId === currentOrg.id) return

      if (orgId === homeOrg.id) {
        goHome()
        return
      }

      const ancestorIndex = path.findIndex((node) => node.id === orgId)
      if (ancestorIndex >= 0) {
        const truncated: OrgPath = path.slice(0, ancestorIndex + 1).map((node) => ({
          id: node.id,
          name: node.name
        }))
        const leaf = truncated.at(-1)!
        cloak({
          id: leaf.id,
          organizationNm: leaf.name,
          path: truncated
        })
        announceViewing(leaf.name)
        navigateAfterSwitch()
        return
      }

      const target = orgById.get(orgId)
      if (!target?.path?.length) return

      cloak(target)
      announceViewing(target.organizationNm ?? '')
      navigateAfterSwitch()
    },
    [currentOrg.id, homeOrg.id, path, orgById, cloak, goHome, announceViewing, navigateAfterSwitch]
  )

  const exitToHome = useCallback(() => {
    if (!isCloaking) return
    goHome()
  }, [isCloaking, goHome])

  return {
    homeOrg,
    currentOrg,
    path,
    children,
    isCloaking,
    canShowBar,
    hasDescendants,
    isTreeLoading,
    switchTo,
    exitToHome
  }
}

export default useOrgContext
